import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getAbandonedCheckoutAnalytics } from '../api/abandonedCheckoutAnalytics';
import type { AbandonedCheckoutAnalytics } from '../types/abandonedCheckoutAnalytics';
import styles from './AbandonedCheckout.module.css';

/**
 * Abandoned Checkout Recovery page.
 *
 * Health + performance monitoring for the abandoned-checkout recovery pipeline:
 * daily abandoned vs. recovered volume, recovery/tag rates, discounts issued,
 * recovered $ value, and a "stuck" pipeline-health signal — all from
 * `/api/admin/abandoned-checkout/analytics/`.
 *
 * Mirrors the Commerce Analytics page conventions: single `days` selector, one
 * network call per window, Recharts area charts, dark theme.
 */

const WINDOWS: ReadonlyArray<{ label: string; days: number }> = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '6m', days: 180 },
];

const TOOLTIP_STYLE = {
  background: '#0d0f14',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 6,
  fontSize: '0.8125rem',
} as const;

function fmtNumber(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return n.toLocaleString();
}

function fmtPercent(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return `${n.toFixed(1)}%`;
}

function fmtMoney(n: number | null | undefined, currency: string): string {
  if (n == null || Number.isNaN(n)) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: n >= 1000 ? 0 : 2,
    }).format(n);
  } catch {
    return `$${n.toLocaleString()}`;
  }
}

function formatRelativeTime(date: Date | null): string {
  if (!date) return 'Never';
  const now = new Date();
  const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diffSec < 5) return 'Just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  return `${Math.floor(diffMin / 60)}h ago`;
}

function tickInterval(pointCount: number): number {
  if (pointCount <= 12) return 0;
  return Math.max(Math.floor(pointCount / 12) - 1, 0);
}

interface ChartPoint {
  label: string;
  value: number;
}

type ChartableSeries = { labels: string[]; data: number[] };

function seriesToPoints(series: ChartableSeries | undefined): ChartPoint[] {
  if (!series || !series.labels || !series.data) return [];
  const n = Math.min(series.labels.length, series.data.length);
  const out: ChartPoint[] = [];
  for (let i = 0; i < n; i++) {
    out.push({ label: series.labels[i], value: series.data[i] });
  }
  return out;
}

interface MetricChartProps {
  title: string;
  headlineValue: string;
  subLabel: string;
  color: string;
  series: ChartableSeries | undefined;
  emptyMessage: string;
}

function MetricChart({
  title,
  headlineValue,
  subLabel,
  color,
  series,
  emptyMessage,
}: MetricChartProps) {
  const points = useMemo(() => seriesToPoints(series), [series]);
  const interval = tickInterval(points.length);
  const gradientId = useMemo(
    () => `abchk-grad-${title.replace(/\s+/g, '-').toLowerCase()}`,
    [title]
  );

  const empty = points.length === 0 || points.every((p) => p.value === 0);

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>{title}</h3>
        <div>
          <span className={styles.sectionTotal}>{headlineValue}</span>
          <span className={styles.sectionSubtotal}>{subLabel}</span>
        </div>
      </div>
      {empty ? (
        <div className={styles.empty}>{emptyMessage}</div>
      ) : (
        <div className={styles.chartWrap}>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart
              data={points}
              margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.55} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255,255,255,0.06)"
              />
              <XAxis
                dataKey="label"
                stroke="#9ca3af"
                tick={{ fontSize: 11 }}
                interval={interval}
              />
              <YAxis
                stroke="#9ca3af"
                tick={{ fontSize: 11 }}
                width={44}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                labelStyle={{ color: '#e5e7eb' }}
                formatter={(value) => {
                  const n = typeof value === 'number' ? value : Number(value);
                  return [Number.isFinite(n) ? n.toLocaleString() : String(value), title];
                }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={2}
                fill={`url(#${gradientId})`}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default function AbandonedCheckout() {
  const [days, setDays] = useState<number>(30);
  const [data, setData] = useState<AbandonedCheckoutAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const load = useCallback(async (windowDays: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getAbandonedCheckoutAnalytics(windowDays);
      setData(result);
      setLastUpdated(new Date());
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Failed to load abandoned-checkout analytics';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(days);
  }, [days, load]);

  const currency = data?.summary.currency ?? 'USD';
  const summary = data?.summary;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Abandoned Checkout Recovery</h1>
          <p className={styles.subtitle}>
            Volume, recovery rate, and pipeline health for the abandoned-checkout
            email flow. Shoppers who reach Shopify's checkout but don't pay are
            tagged in Mailchimp for the recovery journey.
          </p>
        </div>
        <div className={styles.headerActions}>
          <span className={styles.lastUpdated}>
            Updated {formatRelativeTime(lastUpdated)}
          </span>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={() => void load(days)}
            disabled={loading}
          >
            <span
              className={`${styles.refreshIcon} ${loading ? styles.spinning : ''}`}
            >
              ↻
            </span>
            Refresh
          </button>
        </div>
      </div>

      <div className={styles.windowBar}>
        <span className={styles.windowBarLabel}>Window</span>
        <div className={styles.windowPills}>
          {WINDOWS.map((w) => (
            <button
              key={w.days}
              type="button"
              onClick={() => setDays(w.days)}
              className={`${styles.pill} ${days === w.days ? styles.pillActive : ''}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {summary && summary.stuck > 0 && (
        <div className={styles.warning}>
          {summary.stuck.toLocaleString()} checkout(s) are past the recovery
          delay but never reached Mailchimp. This usually means the Shopify
          webhook or Mailchimp tagging is failing — worth investigating.
        </div>
      )}

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {fmtNumber(summary?.total_abandoned)}
          </div>
          <div className={styles.statLabel}>Abandoned ({days}d)</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {fmtPercent(summary?.recovery_rate)}
          </div>
          <div className={styles.statLabel}>Recovered (of emailed)</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {fmtMoney(summary?.value_recovered, currency)}
          </div>
          <div className={styles.statLabel}>Recovered value</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {fmtPercent(summary?.tag_rate)}
          </div>
          <div className={styles.statLabel}>Reached Mailchimp</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {fmtNumber(summary?.discounts_issued)}
          </div>
          <div className={styles.statLabel}>Recovery codes issued</div>
        </div>
        <div className={styles.statCard}>
          <div
            className={`${styles.statValue} ${
              summary
                ? summary.stuck > 0
                  ? styles.statValueBad
                  : styles.statValueGood
                : ''
            }`}
          >
            {fmtNumber(summary?.stuck)}
          </div>
          <div className={styles.statLabel}>Stuck (health)</div>
        </div>
      </div>

      {loading && !data ? (
        <div className={styles.loading}>Loading abandoned-checkout analytics…</div>
      ) : data ? (
        <div className={styles.chartsGrid}>
          <MetricChart
            title="Daily Abandoned"
            headlineValue={fmtNumber(data.abandoned.total)}
            subLabel="checkouts"
            color="#f97316"
            series={data.abandoned}
            emptyMessage="No abandoned checkouts recorded in this window."
          />
          <MetricChart
            title="Daily Recovered"
            headlineValue={fmtNumber(data.recovered.total)}
            subLabel="emailed, then bought"
            color="#22c55e"
            series={data.recovered}
            emptyMessage="No recoveries recorded in this window."
          />
        </div>
      ) : null}

      <div className={styles.notes}>
        <h2 className={styles.notesTitle}>Notes</h2>
        <ul>
          <li>
            A checkout counts as <strong>abandoned</strong> once the shopper
            enters contact info at Shopify checkout but doesn't complete payment.
            Daily series are bucketed by the checkout's creation date (UTC).
          </li>
          <li>
            <strong>Recovered</strong> only counts checkouts that were tagged
            into Mailchimp <em>and</em> then purchased after the tag went on. The
            rate is those recoveries divided by the number we emailed — the
            flow's true conversion rate, not any incidental purchase.
          </li>
          <li>
            <strong>Reached Mailchimp</strong> is the share tagged{' '}
            <code>abandoned-checkout</code> (which triggers the recovery
            journey). <strong>Stuck</strong> are past the recovery delay but
            never tagged — the key signal that the webhook or Mailchimp link
            broke.
          </li>
        </ul>
      </div>
    </div>
  );
}
